import React from 'react';
import { observer } from 'mobx-react';
import { Box, HTMLTable, Paragraph, Switch as CoreSwitch } from 'core/components';
import styled from 'styled-components';
import { Classes } from '@blueprintjs/core';
import { VariableFieldState } from 'app/stores/ude/variables.types';
import type BoardModel from 'app/stores/boards/BoardModel';
import BoardPanelModel from 'app/stores/boards/BoardPanelModel';
import { getGuidedDimensionOptions, resolveGuidedType } from '@kentik/ui-shared/query/measurements/guidedDimensions';
import { useDictionaryMeasurements } from 'app/stores/dictionary/useDictionaryMeasurements';
import DimensionTreeSelector from 'app/views/boards/panels/ude/filters/DimensionTreeSelector';

interface Props {
  board: BoardModel;
  variable: VariableFieldState;
}

const Table = styled(HTMLTable)`
  th,
  td {
    vertical-align: middle;

    &:first-child {
      padding-left: 16px;
    }
    &:last-child {
      padding-right: 16px;
      text-align: right;

      > .${Classes.SWITCH} {
        margin-left: auto;
      }
    }
  }
`;

function BoardVariableMapping({ board, variable }: Props) {
  const variableName = variable.name.value;
  const isGuided = variable.type.value === 'guided';
  const { measurements } = useDictionaryMeasurements();

  const handlePanelToggle = (panel: BoardPanelModel) => {
    panel.toggleVariableMapping(variableName);
    panel.save({}, { draft: true });
  };

  const handlePanelMappingChange = (panel: BoardPanelModel, filterField: string) => {
    panel.setVariableMapping({ variableName, filterField });
    panel.save({}, { draft: true });
  };

  const getGuidedAllowedValues = (panel: BoardPanelModel): string[] | undefined => {
    if (!isGuided) {
      return undefined;
    }
    const measurement = panel.getMeasurement() ?? '';
    const guidedType = resolveGuidedType({
      guidedType: variable.guidedType?.value,
      field: variable.field?.value,
      name: variableName
    });
    return getGuidedDimensionOptions(guidedType, measurement, measurements);
  };

  return (
    <Box>
      {isGuided && (
        <Paragraph mt={2}>
          Some dimensions map differently depending on the type of data source, or traffic direction. We&apos;ve taken a
          guess, but check these over and make sure they behave as expected.
        </Paragraph>
      )}
      <Box mx={-2} mt={2}>
        <Table striped>
          <thead>
            <tr>
              <th>Panel name</th>
              <th>Maps to</th>
              <th>Apply filter</th>
            </tr>
          </thead>
          <tbody>
            {board.panelCollection.models
              .filter((panel) => panel.get('type') === 'ude')
              .map((panel) => {
                const variableMapping = panel.getVariableMapping(variableName);
                const panelMeasurement = panel.getMeasurement() ?? null;
                return (
                  <tr key={panel.id}>
                    <td>{panel.title}</td>
                    <td>
                      <DimensionTreeSelector
                        value={variableMapping?.filterField ?? ''}
                        onChange={(value: string) => handlePanelMappingChange(panel, value)}
                        measurement={panelMeasurement}
                        measurements={measurements}
                        allowedValues={getGuidedAllowedValues(panel)}
                      />
                    </td>
                    <td>
                      <CoreSwitch
                        checked={variableMapping ? !variableMapping.disabled : false}
                        disabled={!variableMapping}
                        onChange={() => handlePanelToggle(panel)}
                      />
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </Table>
      </Box>
    </Box>
  );
}

export default observer(BoardVariableMapping);
